/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...

/*
  Madrone: auto-correct incompatible option combinations (Frame / Drivetrain / Mount).
  When a customer picks an option value whose combination with the current
  selections has no variant, the theme normally shows "Unavailable".
  This keeps the option the customer just clicked (and every option above it),
  and moves each option BELOW it to the first value that makes a real variant.
  If the clicked value can't work with the options above it, the clicked option
  is moved to its first value that does.
  Uses the product's own variant list (/products/<handle>.js); works for
  buttons and dropdowns, product page and quick shop. Sold-out variants are untouched.
*/
(function () {
  var productCache = {};

  function patch(VariantSelects) {
    if (!VariantSelects || VariantSelects.prototype.__mcPatched) return;
    VariantSelects.prototype.__mcPatched = true;
    const originalSetUnavailable = VariantSelects.prototype.setUnavailable;
    VariantSelects.prototype.setUnavailable = function () {
      originalSetUnavailable.apply(this, arguments);
      mcFixCombination(this);
    };
  }

  // One entry per product option: a <select> (dropdowns) or a <fieldset> of radio buttons.
  function optionGroups(root) {
    return Array.from(root.querySelectorAll('fieldset, select'));
  }

  function choices(group) {
    if (group.tagName === 'SELECT') return Array.from(group.options);
    return Array.from(group.querySelectorAll('input'));
  }

  function currentValue(group) {
    if (group.tagName === 'SELECT') return group.value;
    const c = group.querySelector('input:checked');
    return c ? c.value : null;
  }

  // Remember which option group the customer last changed.
  document.addEventListener(
    'change',
    function (e) {
      const t = e.target;
      if (!t || !t.closest) return;
      const el = t.closest('variant-selects, variant-radios, variant-mixed-inputs');
      if (!el) return;
      const groups = optionGroups(el);
      const g = t.tagName === 'SELECT' ? t : t.closest('fieldset');
      const idx = groups.indexOf(g);
      if (idx >= 0) el.__mcLastChanged = idx;
    },
    true
  );

  function getVariants(url) {
    if (!productCache[url]) {
      productCache[url] = fetch(url.split('?')[0] + '.js')
        .then((r) => r.json())
        .then((p) => p.variants || []);
    }
    return productCache[url];
  }

  // Is there any variant matching the given values for options 0..upTo?
  function exists(variants, values, upTo) {
    return variants.some(function (v) {
      for (let j = 0; j <= upTo; j++) if (v.options[j] !== values[j]) return false;
      return true;
    });
  }

  // Pick a value for option i: keep the current one if it works, else the first that works.
  function pick(variants, values, group, i) {
    const opts = choices(group).map((c) => c.value);
    const order = [values[i]].concat(opts.filter((o) => o !== values[i]));
    for (const o of order) {
      const trial = values.slice();
      trial[i] = o;
      if (exists(variants, trial, i)) return o;
    }
    return null;
  }

  async function mcFixCombination(el) {
    try {
      if (el.__mcFixing) return;
      const groups = optionGroups(el);
      if (groups.length < 2 || !el.dataset.url) return;
      el.__mcFixing = true;

      const variants = await getVariants(el.dataset.url);
      if (!variants.length) return;

      const values = groups.map(currentValue);
      const start = values.join('|');
      const n = groups.length;
      if (exists(variants, values, n - 1)) return;

      let changed = el.__mcLastChanged;
      if (typeof changed !== 'number' || changed < 0 || changed >= n) changed = 0;

      // If the clicked value can't work with the options above it, move the clicked option.
      let from = changed + 1;
      if (!exists(variants, values, changed)) {
        const v = pick(variants, values, groups[changed], changed);
        if (v === null) return;
        values[changed] = v;
      }
      for (let i = from; i < n; i++) {
        const v = pick(variants, values, groups[i], i);
        if (v === null) return;
        values[i] = v;
      }

      // Customer changed something while we were working: leave it alone.
      if (groups.map(currentValue).join('|') !== start) return;

      // Apply: set every changed option, then fire one change so the theme loads the variant.
      let last = null;
      groups.forEach(function (g, i) {
        if (currentValue(g) === values[i]) return;
        if (g.tagName === 'SELECT') {
          const opt = Array.from(g.options).find((o) => o.value === values[i]);
          if (opt) opt.disabled = false;
          g.value = values[i];
          last = g;
        } else {
          const input = choices(g).find((c) => c.value === values[i]);
          if (input) {
            input.checked = true;
            last = input;
          }
        }
      });
      if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (e) {
      console.warn('Madrone option auto-correct skipped', e);
    } finally {
      el.__mcFixing = false;
    }
  }

  /*
    Madrone: on products with a "Frame" option, grey out and lock any button in
    every row except the last (Mount) that can't work with the current picks in the
    other rows (e.g. T-Type drivetrains while "not UDH" is chosen). The customer
    has to change the conflicting row themselves; nothing flips automatically.
    The last row (Mount) keeps the theme's own greying plus the auto-correct above.
  */
  const BLOCK = 'data-mc-blocked';

  function hasFrameOption(groups) {
    return groups.some(function (g) {
      const name = g.tagName === 'SELECT' ? g.name : (g.querySelector('input') || {}).name;
      return name === 'Frame';
    });
  }

  function setBlocked(c, blocked) {
    const label = c.id ? document.querySelector('label[for="' + c.id + '"]') : null;
    // Note: the theme hides buttons whose input is :disabled, so we never set .disabled;
    // clicks on blocked buttons are cancelled by the click listener below instead.
    if (blocked) {
      if (c.hasAttribute(BLOCK)) return;
      c.setAttribute(BLOCK, '');
      if (label && !label.classList.contains('is-disabled')) {
        label.classList.add('is-disabled');
        label.setAttribute(BLOCK, '');
      }
    } else if (c.hasAttribute(BLOCK)) {
      c.removeAttribute(BLOCK);
      if (label && label.hasAttribute(BLOCK)) {
        label.classList.remove('is-disabled');
        label.removeAttribute(BLOCK);
      }
    }
  }

  async function mcBlockConflicts(el) {
    try {
      const groups = optionGroups(el);
      const n = groups.length;
      if (n < 3 || !el.dataset.url || !hasFrameOption(groups)) return;
      const variants = await getVariants(el.dataset.url);
      if (!variants.length) return;
      const values = groups.map(currentValue);
      for (let i = 0; i < n - 1; i++) {
        choices(groups[i]).forEach(function (c) {
          if (c.tagName === 'OPTION' || c.checked) return setBlocked(c, false);
          const ok = variants.some(function (v) {
            for (let j = 0; j < n - 1; j++) {
              if (v.options[j] !== (j === i ? c.value : values[j])) return false;
            }
            return true;
          });
          setBlocked(c, !ok);
        });
      }
    } catch (e) {
      console.warn('Madrone option blocking skipped', e);
    }
  }

  // Cancel clicks on blocked buttons (greyed, struck through, still visible).
  document.addEventListener(
    'click',
    function (e) {
      const t = e.target;
      if (!t || !t.closest) return;
      const label = t.closest('label[for]');
      const input = label ? document.getElementById(label.getAttribute('for')) : t.closest('input');
      if (input && input.hasAttribute(BLOCK)) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  let blockTimer = null;
  function scheduleBlock() {
    clearTimeout(blockTimer);
    blockTimer = setTimeout(function () {
      document
        .querySelectorAll('variant-selects, variant-radios, variant-mixed-inputs')
        .forEach(mcBlockConflicts);
    }, 60);
  }
  new MutationObserver(function (muts) {
    if (muts.some((m) => m.type === 'childList')) scheduleBlock();
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('change', scheduleBlock);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleBlock);
  else scheduleBlock();

  ['variant-selects', 'variant-radios', 'variant-mixed-inputs'].forEach(function (tag) {
    customElements.whenDefined(tag).then(function () {
      let C = customElements.get(tag);
      while (C && Object.getPrototypeOf(C) !== HTMLElement && Object.getPrototypeOf(C).prototype.setUnavailable) {
        C = Object.getPrototypeOf(C);
      }
      patch(C);
    });
  });
})();
